export type CanonicalJson =
  | null
  | boolean
  | number
  | string
  | CanonicalJson[]
  | { [key: string]: CanonicalJson };

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function isBigNumberInternalShape(value: Record<string, unknown>): boolean {
  const keys = Object.keys(value).sort();
  return keys.length === 3 && keys[0] === 'c' && keys[1] === 'e' && keys[2] === 's';
}

export function canonicalize(value: unknown): CanonicalJson {
  if (value === null) return null;
  if (typeof value === 'string' || typeof value === 'boolean') return value;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new Error('Cannot canonicalize non-finite number');
    return value;
  }
  if (typeof value === 'bigint') return value.toString();
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map(item => canonicalize(item));
  if (typeof value === 'undefined') throw new Error('Cannot canonicalize top-level undefined');
  if (!isPlainObject(value)) {
    throw new Error(`Cannot canonicalize unsupported value type: ${Object.prototype.toString.call(value)}`);
  }
  if (isBigNumberInternalShape(value)) {
    throw new Error('GalaChain DTO contains BigNumber internals {c,e,s}; convert numeric DTO values to decimal strings before signing');
  }

  const sorted: Record<string, CanonicalJson> = {};
  for (const key of Object.keys(value).sort()) {
    const nested = value[key];
    if (typeof nested === 'undefined') continue;
    sorted[key] = canonicalize(nested);
  }
  return sorted;
}

export function canonicalStringify(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}
